import React, { useEffect, useState } from "react";
import { Modal, Table } from "react-bootstrap";

import CustomButton from "../Button";
import { formateNumber, showToast } from "../../utils/helpers";
import StripePaymentElement from "../StripePaymentElement";
import { Stripe } from "@stripe/stripe-js";
import { CardElementComponent } from "@stripe/react-stripe-js";
import { API } from "../../utils/Apis";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons";

interface StripePaymentModalType {
  show: boolean;
  onClose: Function;
  paymentInfo: {
    id?: string;
    stripeClientSecret?: string;
    paymentId?: string;
    clientSecret?: string;
    grossAmount: string;
    stripeFee: string;
    amount: string;
  };
  bookingInfo: { id: string };
  spaceItem?: { id: string };
  resetForm?: Function;
  onSuccess?: Function;
}

interface StripType {
  stripe: Stripe;
  cardElement: CardElementComponent | undefined;
}

const StripePaymentModal: React.FC<StripePaymentModalType> = ({
  show,
  onClose,
  paymentInfo,
  bookingInfo,
  spaceItem,
  resetForm = () => {},
  onSuccess = () => {},
}) => {
  const [stripInfo, setStripeInfo] = useState<StripType | null>(null);
  const [loading, setLoading] = useState(false);

  const [paymentId, setPaymentId] = useState("");
  const [stripeClientSecret, setStripeClientSecret] = useState("");
  // const [stripePaymentIntentId, setStripePaymentIntentId] = useState("");

  const updateBooking = (type: string, errorMessage = "") => {
    if (type === "success") {
      API.confirmPayment({
        paymentId,
        bookingId: bookingInfo.id,
      })
        .then((res) => {
          if (res.type === "success") {
            showToast("success", res.message);

            if (onSuccess) {
              onSuccess();
            }
          } else {
            showToast("error", res.message);
          }
        })
        .catch((err) => {
          showToast("error", err.message);
        })
        .finally(() => {
          setLoading(false);
          resetForm();
          onClose();
        });
    } else {
      API.failedBookingPayment({
        paymentId,
        bookingId: bookingInfo.id,
        errorMessage,
      })
        .then((res) => {
          if (res.type === "success") {
            showToast("success", res.message);
          } else {
            showToast("error", res.message);
          }
        })
        .catch((err) => {
          showToast("error", err.message);
        })
        .finally(() => {
          setLoading(false);
          onClose();
          resetForm();
        });
    }
  };

  const handlePayment = async () => {
    if (!loading) {
      const stripe = stripInfo ? stripInfo.stripe : null;

      if (stripe) {
        setLoading(true);

        // confirmCardPayment automatically presents the 3D Secure challenge
        // (the bank's authentication popup) when the card requires it, and
        // resolves once the customer has completed — or failed — that step.
        const { paymentIntent, error } = await stripe.confirmCardPayment(
          stripeClientSecret,
          {
            payment_method: { card: stripInfo?.cardElement },
          }
        );

        if (error && error.message) {
          // Includes a failed/abandoned 3DS authentication.
          updateBooking("error", error.message);
        } else if (paymentIntent && paymentIntent.status === "succeeded") {
          updateBooking("success");
          // showToast("success", "Payment successful!");
        } else if (
          paymentIntent &&
          (paymentIntent.status === "processing" ||
            paymentIntent.status === "requires_capture")
        ) {
          // 3DS passed and the charge is settling (or awaiting capture). Treat
          // as success so the booking is confirmed; the webhook reconciles the
          // final state.
          updateBooking("success");
        } else if (paymentIntent && paymentIntent.status === "requires_action") {
          // 3DS could not be completed in this flow.
          updateBooking(
            "error",
            "Card authentication was not completed. Please try again."
          );
        } else {
          // Unexpected terminal state — never leave the button spinning.
          updateBooking(
            "error",
            error?.message || "Payment could not be completed. Please try again."
          );
        }
      } else {
        showToast("error", "Payment form is still loading. Please try again.");
      }
    }
  };

  useEffect(() => {
    const paymentId =
      paymentInfo && paymentInfo.id ? paymentInfo.id : paymentInfo.paymentId;
    const clientSecret =
      paymentInfo && paymentInfo.stripeClientSecret
        ? paymentInfo.stripeClientSecret
        : paymentInfo.clientSecret;

    setPaymentId(paymentId);
    setStripeClientSecret(clientSecret);

    // console.log("bookingInfo Info : ", bookingInfo);
    console.log("Payment Info : ", paymentInfo);
  }, []);

  return (
    <Modal show={show} onHide={() => onClose()}>
      <Modal.Header closeButton>Booking Payment</Modal.Header>
      <Modal.Body>
        <div className="space-infos">
          <div className="space-content">
            <h3>{spaceItem && spaceItem?.tagLine}</h3>
            <div className="d-flex align-items-center justify-content-between">
              <h2>{spaceItem && spaceItem?.name}</h2>
              <div className="space-price">
                ${spaceItem && spaceItem?.ratePerHour}/h
              </div>
            </div>

            {spaceItem && spaceItem?.address && (
              <p>
                <FontAwesomeIcon icon={faLocationDot} /> {spaceItem?.address}
              </p>
            )}

            <div className="row">
              <div className="col-md-6">
                <div className="check-in-wrapper">
                  <label>Check In</label>
                  <p>
                    {bookingInfo &&
                      moment(
                        `${bookingInfo.startDate}T${bookingInfo.startTime}:00`
                      ).format("DD MMM, YYYY")}
                  </p>
                  <p>
                    {bookingInfo &&
                      moment(
                        `${bookingInfo.startDate}T${bookingInfo.startTime}:00`
                      ).format("hh:mm a")}
                  </p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="check-in-wrapper">
                  <label>Check Out</label>
                  <p>
                    {bookingInfo &&
                      moment(
                        `${bookingInfo.endDate}T${bookingInfo.endTime}:00`
                      ).format("DD MMM, YYYY")}
                  </p>
                  <p>
                    {bookingInfo &&
                      moment(
                        `${bookingInfo.endDate}T${bookingInfo.endTime}:00`
                      ).format("hh:mm a")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-booking-infos">
          <div className="mb-4">
            <Table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="text-right">Amount</th>
                </tr>
                <tr>
                  <td>Space Cost</td>
                  <td align="right">
                    ${formateNumber(paymentInfo.grossAmount)}
                  </td>
                </tr>
                <tr>
                  <td>Stripe Platform Fees</td>
                  <td align="right">${formateNumber(paymentInfo.stripeFee)}</td>
                </tr>
                <tr>
                  <td>
                    <b>Total</b>
                  </td>
                  <td align="right">
                    $
                    {paymentInfo.totalAmount
                      ? paymentInfo.totalAmount
                      : formateNumber(paymentInfo.amount)}
                  </td>
                </tr>
              </thead>
            </Table>
          </div>

          <StripePaymentElement
            setCardElement={(
              stripe: Stripe,
              cardElement: CardElementComponent
            ) => setStripeInfo({ stripe, cardElement })}
          />

          <CustomButton
            isLoading={loading}
            title="Pay Now"
            onClick={() => handlePayment()}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default StripePaymentModal;
